// @ts-strict

import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { validationExpressions } from '@app/core/helpers/constants';
import { ICampaignConfigFieldData } from '@app/core/models/core-models';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { isEmpty } from 'lodash';
import { tap } from 'rxjs';

export interface TextInputData {
  value: string;
  system: string;
  hasErrors: boolean;
}

@Component({
  selector: 'app-text-input',
  templateUrl: './text-input.component.html',
  styleUrls: ['./text-input.component.scss']
})
export class TextInputComponent implements OnInit, OnChanges {
  @Input() showLabel = true;
  @Input() fieldInfo?: ICampaignConfigFieldData;
  @Input() submitted = false;
  @Input() configurationData?: ICampaignConfigFieldData[];
  @Input() placeholder?: string;
  @Input() validators?: string[];
  @Input() showAutofocus = false;
  @Input() inputName?: string;
  @Input() inputType = 'text';
  @Input() componentLoaded = false;
  @Input() doNotTranslatePlaceholder = false;
  @Input() doNotTranslateLabel = false;
  @Input() minLength: number | string = 3;
  @Input() maxLength: number | string = 90;
  @Input() min?: number;
  @Input() max?: number;
  @Input() tooltipText?: string;
  @Input() errorMsg = '';
  @Input() autocomplete = 'off';
  @Input() backendError = false;
  @Input() optionalActionText?: string;
  @Input() customBorder?: string;
  @Input() disabled = false;
  @Input() removeWhitespaces = false;
  @Input() prefixText?: string;
  @Output() emitValue: EventEmitter<TextInputData> = new EventEmitter();
  @Output() emitOptionalAction: EventEmitter<void> = new EventEmitter();

  public textForm = this.fb.group({
    text: ['']
  });
  public panelLanguage = '';
  public fieldName = '';
  public isElementFocused = false;
  public placeholderToTranslate = '';

  private isFormFilled = false;

  constructor(
    private localStorageService: LocalStorageService,
    private fb: UntypedFormBuilder,
    private readonly cdr: ChangeDetectorRef
  ) {}

  get configForm() {
    return this.textForm.controls;
  }

  ngOnInit(): void {
    this.placeholderToTranslate = this.setPlaceholder(this.placeholder || '');
    this.panelLanguage = this.localStorageService.getPanelLang();

    if (this.fieldInfo?.translations) {
      this.fieldName = this.fieldInfo!.translations?.find(element => element.lang === this.panelLanguage)?.label || '';
    } else {
      this.fieldName = this.inputName || '';
    }
    if (this.disabled) {
      this.textForm.controls['text'].disable();
    }
    if (this.configurationData?.length) {
      const currentConfig = this.configurationData.find(value => value.system_name === this.fieldInfo?.system_name);
      if (currentConfig) {
        this.textForm.get('text')?.patchValue(currentConfig.config_values);
        // this.textForm.get('text')?.patchValue(decodeHtml(currentConfig.config_values));
      }
      this.getValue();
    }

    if (!isEmpty(this.validators)) {
      this.validators?.map(validator => {
        switch (validator) {
          case 'required':
            this.textForm.get('text')?.setValidators([Validators.required]);
            break;
          case 'name':
            this.textForm.get('text')?.setValidators([Validators.required, Validators.pattern(validationExpressions.namePattern)]);
            break;
          case 'login':
            this.textForm.get('text')?.setValidators([Validators.required, this.validateLogin, Validators.minLength(6)]);
            break;
          case 'email':
            this.textForm.get('text')?.setValidators([Validators.required, Validators.email]);
            break;
          case 'password':
            this.textForm.get('text')?.setValidators([Validators.required, Validators.pattern(validationExpressions.password)]);
            break;
          case 'url':
            this.textForm.get('text')?.setValidators([Validators.required, this.validateURL]);
            break;
          case 'file_size':
            this.textForm.get('text')?.setValidators([Validators.required, Validators.pattern(validationExpressions.fileSizePattern)]);
            break;
          case 'time':
            this.textForm.get('text')?.setValidators([Validators.required, Validators.pattern(validationExpressions.timePattern)]);
            break;
          case 'age':
            this.textForm.get('text')?.setValidators([Validators.required, Validators.pattern(validationExpressions.agePattern)]);
            break;
          case 'phone':
            this.textForm.get('text')?.setValidators([Validators.required, this.validatePhoneNumber]);
            break;
          case 'number':
            this.textForm.get('text')?.setValidators([this.validateNumber]);
            break;
          case 'number-required':
            this.textForm.get('text')?.setValidators([Validators.required, this.validateNumber]);
            break;
          case 'min-length':
            this.textForm.get('text')?.setValidators([Validators.required, Validators.minLength(Number(this.minLength))]);
            break;
          case 'max-length':
            this.textForm.get('text')?.setValidators([Validators.required, Validators.maxLength(Number(this.maxLength))]);
            break;
          case 'min-max':
            this.textForm
              .get('text')
              ?.setValidators([
                Validators.required,
                (control: AbstractControl) => Validators.max(this.max || Infinity)(control),
                (control: AbstractControl) => Validators.min(this.min || 0)(control)
              ]);
            break;
          case 'min':
            this.textForm.get('text')?.setValidators([Validators.required, (control: AbstractControl) => Validators.min(this.min || 0)(control)]);
            break;
          default:
            this.textForm.get('text')?.setValidators([Validators.required]);
            break;
        }
      });
    }
    this.textForm.get('text')?.updateValueAndValidity();

    if (this.removeWhitespaces) {
      this.textForm
        .get('text')
        ?.valueChanges.pipe(
          tap((value: string) => {
            this.textForm.get('text')?.setValue(value.replace(/\s+/g, ''), { emitEvent: false });
          })
        )
        .subscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.disabled) {
      this.textForm?.controls['text'].disable();
    } else {
      this.textForm?.controls['text'].enable();
    }
    // if (this.configurationData?.length && this.componentLoaded && !this.isFormFilled) {
    if (this.configurationData?.length && this.componentLoaded) {
      const currentConfig = this.configurationData.find(value => value.system_name === this.fieldInfo?.system_name);
      if (currentConfig) {
        this.textForm.get('text')?.patchValue(currentConfig.config_values);
        // this.textForm.get('text')?.patchValue(decodeHtml(currentConfig.config_values));
      }
      this.getValue();
    }
  }

  public removeBackendErrors(): void {
    if (this.errorMsg) {
      this.errorMsg = '';
    }
    if (this.backendError) {
      this.backendError = false;
    }
  }

  public getValue(): void {
    this.emitValue.emit({
      value: this.configForm.text.value,
      system: this.fieldInfo?.system_name || '',
      hasErrors: this.configForm.text.invalid
    });
  }

  public onBlurInput(): void {
    this.isElementFocused = false;
    this.cdr.detectChanges();
  }

  public optionalActionHandler(): void {
    this.emitOptionalAction.emit();
  }

  private validateURL(control: AbstractControl): any {
    if (control.value && control.value.match(validationExpressions.url)) {
      return null;
    }
    return { urlInvalid: true };
  }

  private validateLogin(control: AbstractControl): any {
    if (control.value && control.value.match(validationExpressions.loginPattern)) {
      return null;
    }
    return { loginInvalid: true };
  }

  private validateNumber(control: AbstractControl): any {
    if ((control.value && control.value.match(validationExpressions.number)) || control.value?.length === 0) {
      return null;
    }
    return { numberInvalid: true };
  }

  private validatePhoneNumber(control: AbstractControl): any {
    if ((control.value && control.value.match(validationExpressions.phoneNumberPattern)) || control.value.length === 0) {
      return null;
    }
    return { invalidPhoneNumber: true };
  }

  private setPlaceholder(placeholder: string): string {
    let text = 'view_translations.input_label';
    if (placeholder) {
      text = `view_translations.${placeholder}`;
    }
    return text;
  }
}
